import { cn, focusRing } from "@simpro/ui";
import { m } from "motion/react";
import { useId } from "react";
import { NavLink } from "react-router";
import { useMediaQuery } from "@/shared/hooks/useMediaQuery";
import { SLIDE } from "@/shared/motion/presets";

/**
 * On a phone the short names fit four across; on a desktop the header has room to say what each is.
 * Gallery is always there (TDD-SA FR-SA-195, owner request 2026-10-28); a salon that has shared
 * nothing yet shows "No pictures yet" on it rather than losing the way in.
 */
const DESTINATIONS = [
  { to: "/", end: true, short: "Explore", long: "Explore" },
  { to: "/services", end: false, short: "Book", long: "Services" },
  { to: "/gallery", end: false, short: "Gallery", long: "Gallery" },
  { to: "/appointments", end: false, short: "Visits", long: "My bookings" },
] as const;

export interface SwitcherProps {
  /** The folded header's smaller pills. */
  compact?: boolean;
  className?: string;
}

/**
 * The four places a customer goes (design note salon-booking-design-2.md S1, restyled by
 * salon-booking-premium-redesign.md section 5.3 item 1).
 *
 * On a desktop they are ink text links with an accent underline that glides to the current one. On
 * a phone and a tablet they are a segmented control whose ink pill glides the same way; the thumb zone
 * stays free for the floating Book button. Moving between them is a view transition (section 5.4).
 */
export function Switcher({ compact = false, className }: SwitcherProps) {
  const wide = useMediaQuery("(min-width: 1024px)");
  const indicator = useId();
  const links = wide && !compact;

  return (
    <nav
      aria-label="Main"
      className={cn(!links && "rounded-full bg-surface p-[5px] ring-1 ring-edge-subtle ring-inset", className)}
    >
      <ul className={cn(links ? "flex items-center gap-1" : cn("grid grid-cols-4 gap-0.5", compact && "flex"))}>
        {DESTINATIONS.map((destination) => (
          <li key={destination.to} className={cn(compact && "min-w-0 flex-1")}>
            <NavLink
              to={destination.to}
              end={destination.end}
              viewTransition
              className={({ isActive }) =>
                cn(
                  "relative flex items-center justify-center font-semibold whitespace-nowrap transition-colors",
                  links
                    ? "h-11 rounded-full px-3.5 text-[15px] font-medium"
                    : cn("rounded-full", compact ? "h-9 px-2 text-[13px] min-[400px]:px-3" : "h-[2.625rem] px-2 text-sm sm:px-4"),
                  // On a desktop every link is ink, as on the Rosé Arch board; the underline marks the current one.
                  links
                    ? cn("text-heading", !isActive && "hover:text-accent-ink")
                    : isActive
                      ? "text-on-ink"
                      : "text-muted hover:text-heading",
                  focusRing,
                  !links && "focus-inset",
                )
              }
            >
              {({ isActive }) => (
                <>
                  {isActive ? (
                    <m.span
                      aria-hidden="true"
                      layoutId={indicator}
                      transition={SLIDE}
                      className={cn(
                        "absolute",
                        links ? "inset-x-4 bottom-1.5 h-[2px] rounded-full bg-accent-500" : "inset-0 rounded-full bg-ink",
                      )}
                    />
                  ) : null}
                  <span className="relative">{wide ? destination.long : destination.short}</span>
                </>
              )}
            </NavLink>
          </li>
        ))}
      </ul>
    </nav>
  );
}
